import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createSchedule, getSchedule, updateSchedule } from '../../services/schedulerApi';
import { Skeleton, Toast } from '../components';
import {
  JOB_TYPES,
  TARGET_OPTIONS,
  TIMEZONES,
  isPushJobType,
  previewNextRun,
  relativeTime,
  targetKey,
} from '../format';

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Job types whose runs are always full (no incremental variant).
const ALWAYS_FULL = new Set(['pricing', 'bc_products', 'bc_pricing']);

const DEFAULTS = {
  name: '',
  enabled: true,
  job_type: 'customers',
  companies: ['AU', 'NZ'],
  sync_mode: 'full',
  schedule_type: 'interval',
  intervalValue: 30,
  intervalUnit: 'minutes',
  run_times: ['06:00'],
  timezone: 'Australia/Sydney',
  // Push job types only: no pre-selection — the user must explicitly pick
  // at least one {market, environment} target before saving.
  targets: [],
};

export default function ScheduleEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(isEdit ? null : DEFAULTS);
  const [newTime, setNewTime] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    getSchedule(id)
      .then((s) => {
        const minutes = s.interval_minutes || 30;
        const useHours = minutes % 60 === 0 && minutes >= 60;
        setForm({
          name: s.name,
          enabled: s.enabled,
          job_type: s.job_type,
          companies: (s.companies && s.companies.length) ? s.companies : ['AU', 'NZ'],
          sync_mode: s.sync_mode || 'full',
          schedule_type: s.schedule_type,
          intervalValue: useHours ? minutes / 60 : minutes,
          intervalUnit: useHours ? 'hours' : 'minutes',
          run_times: s.run_times || [],
          timezone: s.timezone || 'Australia/Sydney',
          targets: s.targets || [],
        });
      })
      .catch((e) => setToast({ message: e?.response?.data?.detail || 'Could not load schedule', error: true }));
  }, [id, isEdit]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const intervalMinutes = form
    ? Math.round(form.intervalValue * (form.intervalUnit === 'hours' ? 60 : 1))
    : 0;

  const preview = useMemo(() => {
    if (!form) return null;
    const next = previewNextRun({
      scheduleType: form.schedule_type,
      intervalMinutes,
      runTimes: form.run_times,
      timezone: form.timezone,
    });
    if (!next) return null;
    const tzShort = form.timezone.split('/').pop().replace('_', ' ');
    const cadence = form.schedule_type === 'interval'
      ? `Runs every ${form.intervalValue} ${form.intervalUnit === 'hours' ? (form.intervalValue === 1 ? 'hour' : 'hours') : 'minutes'}`
      : `Runs daily at ${[...form.run_times].sort().join(', ')} (${tzShort})`;
    return `${cadence}. ${form.enabled ? `Next run ${relativeTime(next.toISOString())}.` : 'Currently paused.'}`;
  }, [form, intervalMinutes]);

  const isPush = isPushJobType(form?.job_type);
  const alwaysFull = form ? ALWAYS_FULL.has(form.job_type) : false;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Give this schedule a name.';
    if (isPush) {
      if (!form.targets.length) e.targets = 'Select at least one target (market + environment).';
    } else if (!form.companies.length) {
      e.companies = 'Select at least one company.';
    }
    if (form.schedule_type === 'interval') {
      if (!form.intervalValue || form.intervalValue < 1) e.interval = 'Interval must be at least 1.';
      if (intervalMinutes > 10080) e.interval = 'Interval cannot exceed 7 days.';
    } else if (!form.run_times.length) {
      e.run_times = 'Add at least one time of day.';
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const addTime = () => {
    const t = newTime.trim();
    if (!TIME_RE.test(t)) {
      setErrors((er) => ({ ...er, run_times: 'Times must be HH:MM (24-hour).' }));
      return;
    }
    setErrors((er) => ({ ...er, run_times: undefined }));
    set({ run_times: [...new Set([...form.run_times, t])].sort() });
    setNewTime('');
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      enabled: form.enabled,
      job_type: form.job_type,
      companies: isPush ? [] : form.companies,
      sync_mode: alwaysFull ? 'full' : form.sync_mode,
      schedule_type: form.schedule_type,
      interval_minutes: form.schedule_type === 'interval' ? intervalMinutes : null,
      run_times: form.schedule_type === 'daily' ? form.run_times : [],
      timezone: form.timezone,
      targets: isPush ? form.targets.map(({ market, environment }) => ({ market, environment })) : [],
    };
    try {
      if (isEdit) await updateSchedule(id, payload);
      else await createSchedule(payload);
      navigate('/jobs/schedules');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
        : (detail || e.message);
      setToast({ message, error: true });
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="sched-editor">
        <Skeleton height={120} style={{ marginBottom: 16 }} />
        <Skeleton height={260} />
        <Toast message={toast?.message} error={toast?.error} />
      </div>
    );
  }

  return (
    <div className="sched-editor">
      <Link to="/jobs/schedules" className="sched-backlink">← Back to scheduler</Link>
      <div className="sched-topbar">
        <h1>{isEdit ? 'Edit schedule' : 'New schedule'}</h1>
      </div>

      <section className="sched-section">
        <h2>Name</h2>
        <p className="sched-section-hint">Shown on the scheduler overview and in run history.</p>
        <input
          className="sched-input"
          value={form.name}
          placeholder="e.g. Hourly customer changes"
          maxLength={120}
          onChange={(e) => set({ name: e.target.value })}
        />
        {errors.name && <div className="sched-field-error">{errors.name}</div>}
      </section>

      <section className="sched-section">
        <h2>What to sync</h2>
        <p className="sched-section-hint">Pull types bring Epicor data into the middleware/PIM; push types send PIM and middleware data out to BigCommerce.</p>
        <div className="sched-jobtypes" role="radiogroup" aria-label="Sync type">
          {Object.entries(JOB_TYPES).map(([key, jt]) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={form.job_type === key}
              className={`sched-jobtype${form.job_type === key ? ' selected' : ''}`}
              onClick={() => set({ job_type: key, sync_mode: ALWAYS_FULL.has(key) ? 'full' : form.sync_mode })}
            >
              <div className="t">{jt.label}</div>
              {jt.direction && <div className="dir">{jt.direction}</div>}
              <div className="d">{jt.description}</div>
            </button>
          ))}
        </div>
      </section>

      {isPush ? (
        <section className="sched-section">
          <h2>Targets</h2>
          <p className="sched-section-hint">
            Which BigCommerce stores this push goes to. One job is queued per selected target on every run.
            Nothing is pre-selected — choose explicitly.
          </p>
          <div className="sched-chiprow" role="group" aria-label="Targets">
            {TARGET_OPTIONS.map((opt) => {
              const key = targetKey(opt);
              const selected = form.targets.some((t) => targetKey(t) === key);
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={selected}
                  className={`sched-pickchip${selected ? ' selected' : ''}`}
                  onClick={() => set({
                    targets: selected
                      ? form.targets.filter((t) => targetKey(t) !== key)
                      : [...form.targets, { market: opt.market, environment: opt.environment }],
                  })}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {errors.targets && <div className="sched-field-error">{errors.targets}</div>}
        </section>
      ) : (
        <section className="sched-section">
          <h2>Companies</h2>
          <p className="sched-section-hint">Which Epicor companies this schedule covers.</p>
          <div className="sched-chiprow" role="group" aria-label="Companies">
            {['AU', 'NZ'].map((c) => {
              const selected = form.companies.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  aria-pressed={selected}
                  className={`sched-pickchip${selected ? ' selected' : ''}`}
                  onClick={() => set({
                    companies: selected
                      ? form.companies.filter((x) => x !== c)
                      : [...form.companies, c].sort(),
                  })}
                >
                  {c === 'AU' ? 'Australia' : 'New Zealand'}
                </button>
              );
            })}
          </div>
          {errors.companies && <div className="sched-field-error">{errors.companies}</div>}
        </section>
      )}

      <section className="sched-section">
        <h2>How to sync</h2>
        <div className="sched-seg" role="radiogroup" aria-label="Sync mode">
          <button
            type="button"
            role="radio"
            aria-checked={form.sync_mode === 'incremental'}
            className={form.sync_mode === 'incremental' ? 'selected' : ''}
            disabled={alwaysFull}
            onClick={() => set({ sync_mode: 'incremental' })}
          >
            Incremental
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={form.sync_mode === 'full'}
            className={form.sync_mode === 'full' ? 'selected' : ''}
            onClick={() => set({ sync_mode: 'full' })}
          >
            Full
          </button>
        </div>
        <p className="sched-seg-hint">
          {form.job_type === 'pricing'
            ? 'Pricing always runs a full refresh so the nine price tables stay consistent.'
            : form.job_type === 'bc_products' || form.job_type === 'bc_pricing'
              ? 'These pushes are diff-driven: every run compares against BigCommerce and only sends what changed.'
              : form.sync_mode === 'incremental'
                ? 'Changes only — fast and light; ideal for frequent runs. Deletions are reconciled by your full syncs.'
                : 'Everything, every run — slower, but also detects records removed in Epicor. Recommended at least nightly.'}
        </p>
      </section>

      <section className="sched-section">
        <h2>When to run</h2>
        <div className="sched-seg" role="radiogroup" aria-label="Schedule type" style={{ marginBottom: 16 }}>
          <button
            type="button"
            role="radio"
            aria-checked={form.schedule_type === 'interval'}
            className={form.schedule_type === 'interval' ? 'selected' : ''}
            onClick={() => set({ schedule_type: 'interval' })}
          >
            Every interval
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={form.schedule_type === 'daily'}
            className={form.schedule_type === 'daily' ? 'selected' : ''}
            onClick={() => set({ schedule_type: 'daily' })}
          >
            At set times
          </button>
        </div>

        {form.schedule_type === 'interval' ? (
          <>
            <div className="sched-when-row">
              <span className="joiner">Every</span>
              <input
                type="number"
                min="1"
                className="sched-input sched-input--inline"
                style={{ width: 90 }}
                value={form.intervalValue}
                onChange={(e) => set({ intervalValue: Number(e.target.value) })}
              />
              <select
                className="sched-select sched-input--inline"
                style={{ width: 130 }}
                value={form.intervalUnit}
                onChange={(e) => set({ intervalUnit: e.target.value })}
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
              </select>
            </div>
            {errors.interval && <div className="sched-field-error">{errors.interval}</div>}
          </>
        ) : (
          <>
            <div className="sched-when-row">
              <input
                type="time"
                className="sched-input sched-input--inline"
                style={{ width: 130 }}
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTime(); } }}
              />
              <button type="button" className="sched-btn" onClick={addTime}>Add time</button>
              <span className="joiner">in</span>
              <select
                className="sched-select sched-input--inline"
                style={{ width: 200 }}
                value={form.timezone}
                onChange={(e) => set({ timezone: e.target.value })}
              >
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            {form.run_times.length > 0 && (
              <div className="sched-timechips">
                {form.run_times.map((t) => (
                  <span key={t} className="sched-timechip">
                    {t}
                    <button
                      type="button"
                      aria-label={`Remove ${t}`}
                      onClick={() => set({ run_times: form.run_times.filter((x) => x !== t) })}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
            {errors.run_times && <div className="sched-field-error">{errors.run_times}</div>}
          </>
        )}

        {preview && <div className="sched-preview">{preview}</div>}
      </section>

      <section className="sched-section">
        <h2>Status</h2>
        <div className="sched-chiprow" role="group" aria-label="Enabled">
          <button
            type="button"
            aria-pressed={form.enabled}
            className={`sched-pickchip${form.enabled ? ' selected' : ''}`}
            onClick={() => set({ enabled: true })}
          >
            Enabled — runs automatically
          </button>
          <button
            type="button"
            aria-pressed={!form.enabled}
            className={`sched-pickchip${!form.enabled ? ' selected' : ''}`}
            onClick={() => set({ enabled: false })}
          >
            Paused
          </button>
        </div>
      </section>

      <div className="sched-editor-footer">
        <button type="button" className="sched-btn" onClick={() => navigate('/jobs/schedules')} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="sched-btn sched-btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create schedule')}
        </button>
      </div>

      <Toast message={toast?.message} error={toast?.error} />
    </div>
  );
}
